import { FC, useContext, useMemo } from "react";
import dynamic from "next/dynamic";
import {
    afroSignupValidationSchema,
    ConfigContext,
    signupValidationSchema,
    useSiteConfig,
} from "@finbackoffice/site-core";
import classNames from "classnames";
import { ISiteConfigLayouts, SignupLayoutEnum } from "@finbackoffice/fe-core";
import ErrorBoundary from "components/base/error-boundary/ErrorBoundary";
import Loading from "components/base/loading/Loading";
import styles from "./signup.module.sass";

const SignupDetailsForm = dynamic(() => import("./details/SignupDetailsForm"), {
    loading: () => (
        <Loading wrapperClassName={classNames(styles.loading, styles.loadingDetailsSignup)} />
    ),
    ssr: false,
});

const CpfSignup = dynamic(() => import("./cpf/CpfSignup"), {
    loading: () => (
        <Loading wrapperClassName={classNames(styles.loading, styles.loadingCpfSignup)} />
    ),
    ssr: false,
});

const CpfSignupOneStep = dynamic(() => import("./cpf-one-step/CpfSignupOneStep"), {
    loading: () => <Loading wrapperClassName={styles.loading} />,
    ssr: false,
});

const CpfSignupKyc = dynamic(() => import("./cpf-kyc/CpfSignupKyc"), {
    loading: () => <Loading wrapperClassName={styles.loading} />,
    ssr: false,
});

const Signup: FC = () => {
    const siteLayoutsConfig = useSiteConfig<ISiteConfigLayouts>("layouts");
    const { siteConfigReady } = useContext(ConfigContext);

    const renderContent = useMemo(() => {
        switch (siteLayoutsConfig.signup) {
            case SignupLayoutEnum.CPFSignup:
                return (
                    <ErrorBoundary name={CpfSignup.name}>
                        <CpfSignup />
                    </ErrorBoundary>
                );
            case SignupLayoutEnum.CPFSignup1Step:
                return (
                    <ErrorBoundary name={CpfSignupOneStep.name}>
                        <CpfSignupOneStep />
                    </ErrorBoundary>
                );
            case SignupLayoutEnum.CPFSignupKyc:
                return (
                    <ErrorBoundary name={CpfSignupOneStep.name}>
                        <CpfSignupKyc />
                    </ErrorBoundary>
                );
            case SignupLayoutEnum.Afro:
                return (
                    <ErrorBoundary name={SignupDetailsForm.name}>
                        <SignupDetailsForm
                            title="signup_formTitle"
                            validationSchema={afroSignupValidationSchema}
                        />
                    </ErrorBoundary>
                );
            default:
                return (
                    <ErrorBoundary name={SignupDetailsForm.name}>
                        <SignupDetailsForm
                            title="signup_formTitle"
                            validationSchema={signupValidationSchema}
                        />
                    </ErrorBoundary>
                );
        }
    }, [siteLayoutsConfig.signup]);

    return <>{siteConfigReady ? renderContent : <Loading />}</>;
};

export default Signup;
